import { Component, HostListener, OnDestroy } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faComments, faPowerOff } from '@fortawesome/free-solid-svg-icons';
import { NgxSpinnerModule } from 'ngx-spinner';
import { Subject, from } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { TchatComponent } from './components/tchat/tchat-component';
import { Authenticate } from './models/server/authenticate';
import { DialogService } from './services/actions/dialog.service';
import { NotificationService } from './services/actions/notification.service';
import { WebBrowserTabBlinkerService } from './services/core/web-browser-tab-blinker-service';
import { AuthService } from './services/http/auth.service';
import { UsersService } from './services/http/users.service';
import { ConnectedUsersStateService } from './services/state/connected-users-state.service';
import { SessionTimeoutService } from './services/state/session-timeout.service';
import { TChatStateService } from './services/state/tchat-state.service';
import { ViewsStateService } from './services/state/views-state.service';
import { WebSocketNotificationService } from './services/ws/websocket-notification.service';
import { WebSocketTchatService } from './services/ws/websocket-tchat.service';

import { Location } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { BannerMenuComponent } from './components/banner/banner-menu/banner-menu.component';
import { MenuSettingsComponent } from './components/banner/banner-settings-menu/banner-settings-menu.component';
import { UserProfileComponent } from './components/banner/user-profile/user-profile.component';
import { TabSelectionType } from './models/enums/tab-selection-type';
import { Rights } from './models/server/enums/rights';
import { ServiceCodes } from './models/server/enums/service-codes';
import { ThemesService } from './services/core/themes.service';
import { ProfileSettingsStateService } from './services/state/profile-settings-state.service';
import { TabSelectedStateService } from './services/state/tab-selected-state.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [
        MenuSettingsComponent,
        UserProfileComponent,
        MatTooltipModule,
        MatBadgeModule,
        FaIconComponent,
        BannerMenuComponent,
        RouterLink,
        RouterOutlet,
        NgxSpinnerModule,
        MatIconModule,
    ],
})
export class AppComponent implements OnDestroy {
    title = 'Hub4B FrontOffice';

    public ServiceCodes = ServiceCodes;
    public powerOffIcon = faPowerOff;
    public tchatIcon = faComments;

    /**
     * Utilisé pour gérer la désincription des observables lorsque le composant est détruit.
     */
    protected componentDestroyed$: Subject<void> = new Subject<void>();

    /**
     * Utilisateur connecté
     */
    public userConnected: Authenticate = null;

    /**
     * Nombre de notifications de conversations Tchat.
     */
    public tchatNotification: number = 0;

    /**
     * Delai (en secondes) avant l'expiration de la session pour afficher la notification à l'utilisateur.
     */
    public sessionNotificationDelay = 60;

    /**
     * Valeur indiquant si la bannière doit être masquée.
     */
    public hideBanner = false;

    /**
     * Texte affiché dans le bandeau pour indiquer quel onglet est selectionné.
     */
    public selectedTabLabel = '';

    /**
     * Type de l'onglet selectionné.
     */
    public selectedTabType: TabSelectionType = null;

    public isDarkMode: boolean;

    /**
     * Constructeur
     */
    constructor(
        private router: Router,
        protected notificationService: NotificationService,
        private connectedUserStateService: ConnectedUsersStateService,
        private usersService: UsersService,
        protected authService: AuthService,
        protected dialogService: DialogService,
        protected webSocketNotificationService: WebSocketNotificationService,
        private webSocketTchatService: WebSocketTchatService,
        private tchatService: TChatStateService,
        private bottomSheet: MatBottomSheet,
        private timeoutService: SessionTimeoutService,
        private webBrowserTabBlinkerService: WebBrowserTabBlinkerService,
        public viewsStateService: ViewsStateService,
        public tabSelectedStateService: TabSelectedStateService,
        private location: Location,
        private themesService: ThemesService,
        profileSettingsStateService: ProfileSettingsStateService
    ) {
        console.log(location.path());
        if (location.path().startsWith('/tracking')) {
            this.hideBanner = true;
        } else {
            this.hideBanner = false;
        }

        this.themesService.changeTheme(this.themesService.getCurrentTheme(), true);

        // Abonnement au changement d'état de l'utilisateur connecté
        this.connectedUserStateService.onUserConnectedChange$.pipe(takeUntil(this.componentDestroyed$)).subscribe((x) => {
            if (!!x && (this.userConnected === null || this.userConnected.userId !== x.userId)) {
                this.webSocketNotificationService.startSocket(x.organization.id, x.login);
                this.webSocketTchatService.startSocket(x.organization.id, x.login);

                let followFolderRight = connectedUserStateService.hasRight(Rights.FollowFolder);
                let showfollowFolder = profileSettingsStateService.getProfileSettings().showFolderFollow;

                if (followFolderRight === true && showfollowFolder == true) {
                    this.tabSelectedStateService.setSelectedTabType(TabSelectionType.MyFolders);
                } else {
                    this.tabSelectedStateService.setSelectedTabType(TabSelectionType.ViewFolders);
                }
            }

            // Dans le cas où une notification d'expiration de session s'est affichée, arrête le clignotement de l'onglet si la session a expirée.
            this.webBrowserTabBlinkerService.stop();

            this.userConnected = x;
        });

        // Abonnement au notification webSocket
        this.webSocketNotificationService.message$.pipe(takeUntil(this.componentDestroyed$)).subscribe((notification) => {
            this.notificationService.display(notification);
        });

        // Abonnement au notification du tchat
        this.tchatService.notifications$.pipe(takeUntil(this.componentDestroyed$)).subscribe((notifications) => {
            this.tchatNotification = 0;
            notifications.forEach((n) => {
                this.tchatNotification = this.tchatNotification + n.notification;
            });
        });

        this.subscribeToSessionTimeout();

        this.subscribeToTabSelectionEvents();
    }

    /**
     * * S'abonne aux événements de changement de l'ongelt selectionné.
     */
    private subscribeToTabSelectionEvents() {
        this.tabSelectedStateService.onTabSelectionChange$.pipe(takeUntil(this.componentDestroyed$)).subscribe((tabType) => {
            this.selectedTabType = tabType;

            if (tabType !== null) {
                if (tabType === TabSelectionType.MyFolders) {
                    this.selectedTabLabel = 'Mes dossiers';
                } else if (tabType === TabSelectionType.ViewFolders) {
                    if (this.viewsStateService.getSelectedView()) {
                        this.selectedTabLabel = this.viewsStateService.getSelectedView().name;
                    }
                } else if (tabType === TabSelectionType.FolderDetail) {
                    this.selectedTabLabel = '';
                }
            } else {
                this.selectedTabLabel = '';
            }
        });
    }

    @HostListener('window:beforeunload', ['$event'])
    onBeforeUnload(event) {
        if (this.userConnected) {
            this.authService.wildLogoff(this.userConnected.token.accessToken);
        }

        this.ngOnDestroy();
    }

    ngOnInit() {
        this.themesService.currentTheme$.pipe(takeUntil(this.componentDestroyed$)).subscribe((theme) => {
            this.isDarkMode = theme.includes('dark') ? true : false;
        });
    }

    /**
     * Appelée à la destruction du composant.
     */
    ngOnDestroy(): void {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    /**
     * Gestion de la deconnexion
     */
    public logoff(): void {
        this.authService
            .logoff(this.userConnected.token.accessToken)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((X) => {
                this.router.navigate(['/login']);
            });
    }

    /**
     * Ouverture du tchat
     */
    public openTchat() {
        if (this.connectedUserStateService.hasServiceSubscription(ServiceCodes.HubTchat)) {
            this.bottomSheet.open(TchatComponent, { backdropClass: 'tchat-overlay', panelClass: 'tchat-mat-bottom-sheet-container' });
        } else {
            this.notificationService.displayWarning('Abonnement requis.', "Vous n'êtes pas abonné au service de Tchat. Veuillez contacter votre administrateur.");
        }
    }

    public onThemeChange() {
        this.themesService.changeTheme(this.isDarkMode ? 'avm-light-theme' : 'avm-dark-theme', true);
    }

    private subscribeToSessionTimeout() {
        this.timeoutService.notificationDelay = this.sessionNotificationDelay;
        this.timeoutService.timeoutExpired.pipe(takeUntil(this.componentDestroyed$)).subscribe((res) => {
            console.log('Vérifie auprès du serveur si le token a été prolongé...');
            this.authService.checkToken().subscribe(
                (x) => {
                    if (this.timeoutService.calculateRemainingSessionTime(x.expireOn) <= this.timeoutService.notificationDelay * 1000) {
                        console.log("Le token n'a pas été prolongé");
                        console.log("La session est sur le point d'expirer.");
                        this.notifyUserAboutSessionTimeout();
                    } else {
                        console.log('La session a été prolongée');
                        // La session a du être prolongée par une action utilisateur --> on reinitialise le timer avec la nouvelle date d'expiration.
                        this.timeoutService.startTimer(x.expireOn);
                    }
                },
                catchError((error) => {
                    this.connectedUserStateService.setUserConnected(null);
                    this.router.navigate(['/login']);
                    throw error;
                })
            );
        });
    }

    private notifyUserAboutSessionTimeout() {
        const modalPromise = this.dialogService.openSessionTimeout(this.connectedUserStateService.getUserConnected().token.expireOn, this.sessionNotificationDelay);
        const newObservable = from(modalPromise);
        newObservable.subscribe(
            (resp) => {
                if (resp === true) {
                    console.log("L'utilisateur a étendu la session.");
                    this.authService.keepAliveToken().subscribe((x) => {
                        this.timeoutService.startTimer(this.connectedUserStateService.getUserConnected().token.expireOn);
                    });
                } else {
                    console.log("L'utilisateur n'a pas étendu la session.");
                    this.dialogService.closeAll();
                    this.logoff();
                }
            },
            (reason) => {
                console.log("L'utilisateur n'a pas répondu.");
                this.dialogService.closeAll();
                this.logoff();
            }
        );
    }
}
